import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import logger from "@/lib/logger";
import type {
  ConversationTree,
  TreeNode,
  ConversationStrategy,
  PatchBatch,
  TreeMessage,
} from "@/components/reasoning/types";
import {
  countFutureNodesFromCurrent,
  getFutureNodeDepths,
  getFuturePredictionShape,
} from "./orchestratorTreeUtils";
import { computeSmartExpandedIds } from "@/components/reasoning/utils/treeCollapse";

// ============================================================================
// State Interface
// ============================================================================

export interface OrchestratorTreeState {
  /** The current conversation tree */
  tree: ConversationTree | null;
  /** Loading state for initial tree fetch */
  isLoading: boolean;
  /** Error message if tree loading/update failed */
  error: string | null;
  /** Set of expanded node IDs (for collapse/expand) */
  expandedNodeIds: string[];
  /** Currently selected node ID for detail panel */
  selectedNodeId: string | null;
  /** Whether detail panel is open */
  isDetailPanelOpen: boolean;
  /** Set of node IDs whose inline detail is expanded */
  detailExpandedNodeIds: string[];
  /** Whether strategy panel is collapsed */
  isStrategyPanelCollapsed: boolean;
  /** Version counter for optimistic updates */
  localVersion: number;
  /** Whether the client should request a full tree refresh */
  needsFullTreeRefresh: boolean;
  /** Whether more future predictions are needed (insufficient depth) */
  needsMorePredictions: boolean;
  /** Whether backend needs tree seed from frontend (worker restart recovery) */
  needsTreeSeed: boolean;
  /** Timestamp of last user interaction with the tree view */
  lastInteractionTime: number;
  /** Whether user is currently interacting with the view */
  isUserInteracting: boolean;
  /** Whether auto-centering should be enabled (after inactivity timeout) */
  autoViewEnabled: boolean;
  /** Previous current node ID for transition detection */
  previousCurrentNodeId: string | null;
  /** Timestamp of last node transition */
  lastTransitionTime: number;
  /** Timestamp of last step progression (currentNodeId change only, NOT general patches) */
  lastTreeUpdateTime: number;
  /** Whether stuck detection is enabled (disabled during initial load) */
  stuckDetectionEnabled: boolean;
  /** Count of consecutive stuck refresh attempts (for backoff) */
  stuckRefreshAttempts: number;
}

const initialState: OrchestratorTreeState = {
  tree: null,
  isLoading: false,
  error: null,
  expandedNodeIds: [],
  selectedNodeId: null,
  isDetailPanelOpen: false,
  detailExpandedNodeIds: [],
  isStrategyPanelCollapsed: false,
  localVersion: 0,
  needsFullTreeRefresh: false,
  needsMorePredictions: false,
  needsTreeSeed: false,
  lastInteractionTime: 0,
  isUserInteracting: false,
  autoViewEnabled: true,
  previousCurrentNodeId: null,
  lastTransitionTime: 0,
  lastTreeUpdateTime: 0,
  stuckDetectionEnabled: false,
  stuckRefreshAttempts: 0,
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Recursively find a node by ID in the tree
 */
function findNodeById(node: TreeNode, nodeId: string): TreeNode | null {
  if (node.id === nodeId) {
    return node;
  }
  for (const child of node.children || []) {
    const found = findNodeById(child, nodeId);
    if (found) return found;
  }
  return null;
}

/**
 * Collect all node IDs from the tree (for expanding all by default)
 */
function collectAllNodeIds(node: TreeNode): string[] {
  const ids: string[] = [node.id];
  for (const child of node.children || []) {
    ids.push(...collectAllNodeIds(child));
  }
  return ids;
}

/**
 * Walk the tree and ensure exactly one node has status='current' — the one
 * referenced by tree.currentNodeId. Any other 'current' node gets coerced:
 * - Ancestors of currentNodeId → 'past' (already-traveled)
 * - Everything else → 'future' (predictions/non-traveled)
 *
 * The backend has the same safety net (`_enforce_single_current` in
 * tree_analyzer.py); this is the frontend mirror so persisted snapshots
 * loaded via setTree get the same repair pass. Without it, an old snapshot
 * with two 'current' nodes baked in would render two blue nodes until the
 * next backend update happened to overwrite them.
 *
 * Mutates the tree in place. Returns the number of fixes applied.
 */
function enforceSingleCurrent(tree: ConversationTree): number {
  const targetId = tree.currentNodeId;
  if (!targetId) return 0;

  const ancestorIds = new Set<string>();
  let foundTarget = false;

  function findPath(node: TreeNode): boolean {
    if (node.id === targetId) {
      foundTarget = true;
      return true;
    }
    for (const child of node.children || []) {
      if (findPath(child)) {
        ancestorIds.add(node.id);
        return true;
      }
    }
    return false;
  }

  findPath(tree.rootNode);
  if (!foundTarget) {
    // currentNodeId missing from tree — let the existing reset path handle it.
    return 0;
  }

  let fixes = 0;
  function coerce(node: TreeNode): void {
    if (node.status === "current" && node.id !== targetId) {
      node.status = ancestorIds.has(node.id) ? "past" : "future";
      fixes++;
    } else if (node.id === targetId && node.status !== "current") {
      node.status = "current";
      fixes++;
    }
    for (const child of node.children || []) {
      coerce(child);
    }
  }
  coerce(tree.rootNode);
  return fixes;
}

/**
 * Check if a patch path targets a node in the tree structure.
 * Paths targeting nodes contain "children" segments followed by an index.
 * Examples: /rootNode/children/0, /rootNode/children/0/children/1
 */
function isNodePath(pathSegments: string[]): boolean {
  // If path contains "children" segment followed by an index, it targets node structure
  for (let i = 0; i < pathSegments.length - 1; i++) {
    if (pathSegments[i] === "children" && /^\d+$/.test(pathSegments[i + 1])) {
      // If this is the last meaningful segment pair, it's targeting a node
      // e.g., /rootNode/children/0 or /rootNode/children/0/children/1
      if (i + 2 === pathSegments.length ||
          (i + 2 < pathSegments.length && pathSegments[i + 2] === "children")) {
        return true;
      }
    }
  }
  return false;
}

const DEFAULT_PATCH_OPTIONS = {
  c: false,
  p: true,  // createParents: allow ADD ops to create intermediate paths (e.g. children arrays)
  s: null,
  t: null,
  a: true,
  cs: true,
  r: false,
  sd: true,
  id: null,
} as const;

function decodePointerSegment(segment: string): string {
  return segment.replace(/~1/g, "/").replace(/~0/g, "~");
}

function parsePointer(path: string): string[] {
  if (!path) return [];
  const normalized = path.startsWith("/") ? path.slice(1) : path;
  if (!normalized) return [];
  return normalized.split("/").map(decodePointerSegment);
}

function isIndexSegment(segment: string): boolean {
  return segment === "-" || /^[0-9]+$/.test(segment);
}

function getValueAtPath(target: any, pathSegments: string[]): any {
  let current = target;
  for (const segment of pathSegments) {
    if (Array.isArray(current)) {
      const index = segment === "-" ? current.length : Number(segment);
      if (!Number.isInteger(index) || index < 0 || index >= current.length) {
        return undefined;
      }
      current = current[index];
      continue;
    }
    if (!current || typeof current !== "object") {
      return undefined;
    }
    if (!(segment in current)) {
      return undefined;
    }
    current = current[segment];
  }
  return current;
}

function getContainer(
  target: any,
  pathSegments: string[],
  createParents: boolean
): { parent: any; key: string } | null {
  if (pathSegments.length === 0) {
    return { parent: null, key: "" };
  }

  let current = target;
  for (let i = 0; i < pathSegments.length - 1; i++) {
    const segment = pathSegments[i];
    const nextSegment = pathSegments[i + 1];

    if (Array.isArray(current)) {
      const index = segment === "-" ? current.length : Number(segment);
      if (!Number.isInteger(index) || index < 0 || index > current.length) {
        return null;
      }
      if (current[index] === undefined) {
        if (!createParents) return null;
        const nextValue = isIndexSegment(nextSegment) ? [] : {};
        if (index === current.length) {
          current.push(nextValue);
        } else {
          current[index] = nextValue;
        }
      }
      current = current[index];
      continue;
    }

    if (!current || typeof current !== "object") {
      return null;
    }

    if (!(segment in current) || current[segment] === undefined) {
      if (!createParents) return null;
      current[segment] = isIndexSegment(nextSegment) ? [] : {};
    }
    current = current[segment];
  }

  return { parent: current, key: pathSegments[pathSegments.length - 1] };
}

function parseJsonValue(value: string | null): any {
  if (value === null) return null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function deepEqual(a: any, b: any): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}

function isDuplicateItem(
  items: any[],
  value: any,
  idFields: string[] | null
): boolean {
  if (!Array.isArray(items)) return false;
  if (idFields && value && typeof value === "object") {
    return items.some((item) => {
      if (!item || typeof item !== "object") return false;
      return idFields.every((field) => item[field] === value[field]);
    });
  }
  return items.some((item) => deepEqual(item, value));
}

/**
 * Validate tree has sufficient future nodes after an update.
 * Returns true if more predictions are needed.
 *
 * Requirement: 3 primary predictions, 3 children under the top primary,
 * one child under each non-top primary, and two extra single-child steps
 * under the highest-probability top child.
 */
function validateFutureNodes(tree: ConversationTree): boolean {
  const MIN_FUTURE_NODES = 10;
  const REQUIRED_PRIMARY_PREDICTIONS = 3;
  const REQUIRED_TOP_PRIMARY_CHILDREN = 3;
  const REQUIRED_TOP_BEST_CHILD_PATH_DEPTH = 4;
  const futureCount = countFutureNodesFromCurrent(tree);
  const depths = getFutureNodeDepths(tree);
  const shape = getFuturePredictionShape(tree);

  if (futureCount < MIN_FUTURE_NODES) {
    const deficit = MIN_FUTURE_NODES - futureCount;
    logger.debug("OrchestratorTree", "Insufficient future nodes", {
      futureCount,
      required: MIN_FUTURE_NODES,
      deficit,
      depths,
      shape,
    });
    return true; // Need more predictions
  }

  if (
    shape.primaryCount < REQUIRED_PRIMARY_PREDICTIONS ||
    shape.primariesWithChild < REQUIRED_PRIMARY_PREDICTIONS ||
    shape.topPrimaryChildCount < REQUIRED_TOP_PRIMARY_CHILDREN ||
    shape.topBestChildPathDepth < REQUIRED_TOP_BEST_CHILD_PATH_DEPTH
  ) {
    logger.debug("OrchestratorTree", "Insufficient focused prediction shape", {
      futureCount,
      depths,
      shape,
      requirement:
        "3 primary predictions, 3 children under top primary, best top child depth 4",
    });
    return true; // Need more predictions
  }

  logger.debug("OrchestratorTree", "Future nodes validation passed", {
    futureCount,
    depths,
    shape,
  });
  return false; // Sufficient predictions
}

interface PatchApplyResult {
  tree: ConversationTree;
  ok: boolean;
  failureReason?: string;
}

/**
 * Apply a PatchAiz batch to the tree (compact RFC 6902 + extended ops).
 */
function applyPatchToTree(
  tree: ConversationTree,
  patch: PatchBatch,
  nextVersion?: number
): PatchApplyResult {
  // Clone the tree for immutable update
  const originalTree = tree;
  let updatedTree = JSON.parse(JSON.stringify(tree)) as ConversationTree;
  const defaults = { ...DEFAULT_PATCH_OPTIONS, ...(patch.d || {}) };
  let failureReason: string | null = null;
  const fail = (reason: string) => {
    failureReason = reason;
  };

  for (const op of patch.ops) {
    if (failureReason) break;
    try {
      const pathSegments = parsePointer(op.p);
      const createParents = Boolean(defaults.p);

      switch (op.o) {
        case "add": {
          const value = parseJsonValue(op.v);
          if (pathSegments.length === 0) {
            updatedTree = value as ConversationTree;
            break;
          }
          const container = getContainer(updatedTree, pathSegments, createParents);
          if (!container) {
            fail("missing_path");
            break;
          }
          if (Array.isArray(container.parent)) {
            const index = container.key === "-" ? container.parent.length : Number(container.key);
            if (!Number.isInteger(index) || index < 0) {
              fail("invalid_index");
              break;
            }
            if (defaults.sd && isDuplicateItem(container.parent, value, defaults.id)) break;
            if (container.key === "-") {
              container.parent.push(value);
            } else if (index > container.parent.length) {
              fail("invalid_index");
              break;
            } else if (index === container.parent.length) {
              container.parent.push(value);
            } else {
              container.parent.splice(index, 0, value);
            }
          } else if (container.parent && typeof container.parent === "object") {
            container.parent[container.key] = value;
          } else {
            fail("missing_path");
          }
          break;
        }
        case "replace": {
          const value = parseJsonValue(op.v);
          if (pathSegments.length === 0) {
            updatedTree = value as ConversationTree;
            break;
          }
          const container = getContainer(updatedTree, pathSegments, createParents);
          if (!container) {
            fail("missing_path");
            break;
          }
          if (Array.isArray(container.parent)) {
            const index = Number(container.key);
            if (!Number.isInteger(index) || index < 0) {
              fail("invalid_index");
              break;
            }
            if (index >= container.parent.length) {
              if (defaults.c && index === container.parent.length) {
                container.parent.push(value);
              } else {
                fail("missing_path");
              }
              break;
            }
            container.parent[index] = value;
          } else if (container.parent && typeof container.parent === "object") {
            if (!defaults.c && !(container.key in container.parent)) {
              fail("missing_path");
              break;
            }
            container.parent[container.key] = value;
          } else {
            fail("missing_path");
          }
          break;
        }
        case "remove": {
          if (pathSegments.length === 0) {
            fail("missing_path");
            break;
          }
          // PROTECTION: Never allow node deletion - AI should only update node state/properties
          // Skip remove operations that target nodes in the tree structure
          if (isNodePath(pathSegments)) {
            logger.warn("OrchestratorTree", "Blocked node removal - nodes cannot be deleted, only updated", {
              path: op.p,
            });
            // Don't fail, just skip this operation silently
            break;
          }
          const container = getContainer(updatedTree, pathSegments, false);
          if (!container) {
            fail("missing_path");
            break;
          }
          if (Array.isArray(container.parent)) {
            const index = Number(container.key);
            if (!Number.isInteger(index) || index < 0 || index >= container.parent.length) {
              fail("invalid_index");
              break;
            }
            container.parent.splice(index, 1);
          } else if (container.parent && typeof container.parent === "object") {
            if (!(container.key in container.parent)) {
              fail("missing_path");
              break;
            }
            delete container.parent[container.key];
          } else {
            fail("missing_path");
          }
          break;
        }
        case "move":
        case "copy": {
          if (!op.f) {
            fail("missing_path");
            break;
          }
          const fromSegments = parsePointer(op.f);
          const value = getValueAtPath(updatedTree, fromSegments);
          if (value === undefined) {
            fail("missing_path");
            break;
          }
          if (op.o === "move") {
            const fromContainer = getContainer(updatedTree, fromSegments, false);
            if (fromContainer) {
              if (Array.isArray(fromContainer.parent)) {
                const index = Number(fromContainer.key);
                if (!Number.isInteger(index) || index < 0 || index >= fromContainer.parent.length) {
                  fail("invalid_index");
                  break;
                }
                fromContainer.parent.splice(index, 1);
              } else if (fromContainer.parent && typeof fromContainer.parent === "object") {
                if (!(fromContainer.key in fromContainer.parent)) {
                  fail("missing_path");
                  break;
                }
                delete fromContainer.parent[fromContainer.key];
              }
            } else {
              fail("missing_path");
              break;
            }
          }
          const targetContainer = getContainer(updatedTree, pathSegments, createParents);
          if (!targetContainer) {
            fail("missing_path");
            break;
          }
          if (Array.isArray(targetContainer.parent)) {
            const index = targetContainer.key === "-" ? targetContainer.parent.length : Number(targetContainer.key);
            if (!Number.isInteger(index) || index < 0) {
              fail("invalid_index");
              break;
            }
            const valueCopy = JSON.parse(JSON.stringify(value));
            if (defaults.sd && isDuplicateItem(targetContainer.parent, valueCopy, defaults.id)) break;
            if (targetContainer.key === "-") {
              targetContainer.parent.push(valueCopy);
            } else if (index > targetContainer.parent.length) {
              fail("invalid_index");
              break;
            } else if (index === targetContainer.parent.length) {
              targetContainer.parent.push(valueCopy);
            } else {
              targetContainer.parent.splice(index, 0, valueCopy);
            }
          } else if (targetContainer.parent && typeof targetContainer.parent === "object") {
            targetContainer.parent[targetContainer.key] = JSON.parse(JSON.stringify(value));
          } else {
            fail("missing_path");
          }
          break;
        }
        case "test": {
          const expected = parseJsonValue(op.v);
          const actual = getValueAtPath(updatedTree, pathSegments);
          if (!deepEqual(actual, expected)) {
            logger.warn("OrchestratorTree", "Patch test failed", {
              path: op.p,
              expected,
              actual,
            });
            failureReason = "test_failed";
          }
          break;
        }
        case "str-append":
        case "str-prepend": {
          const container = getContainer(updatedTree, pathSegments, createParents);
          if (!container) {
            fail("missing_path");
            break;
          }
          const currentValue = getValueAtPath(updatedTree, pathSegments);
          if (typeof currentValue !== "string" || op.v === null) {
            fail("type_mismatch");
            break;
          }
          const separator = defaults.s ?? "";
          const nextValue =
            op.o === "str-append"
              ? currentValue
                ? `${currentValue}${separator}${op.v}`
                : op.v
              : currentValue
                ? `${op.v}${separator}${currentValue}`
                : op.v;
          if (container.parent === null) {
            fail("missing_path");
          } else if (Array.isArray(container.parent)) {
            const index = Number(container.key);
            if (!Number.isInteger(index) || index < 0 || index >= container.parent.length) {
              fail("invalid_index");
              break;
            }
            container.parent[index] = nextValue;
          } else if (container.parent && typeof container.parent === "object") {
            container.parent[container.key] = nextValue;
          } else {
            fail("missing_path");
          }
          break;
        }
        case "str-replace":
        case "str-remove": {
          const container = getContainer(updatedTree, pathSegments, createParents);
          if (!container || op.v === null) {
            fail("missing_path");
            break;
          }
          const currentValue = getValueAtPath(updatedTree, pathSegments);
          if (typeof currentValue !== "string") {
            fail("type_mismatch");
            break;
          }
          let payload: { find?: string; replace?: string } = {};
          try {
            payload = JSON.parse(op.v);
          } catch {
            fail("invalid_value");
            break;
          }
          if (!payload.find) {
            fail("invalid_value");
            break;
          }
          const replacement = op.o === "str-remove" ? "" : payload.replace ?? "";
          let nextValue = currentValue;
          if (defaults.r) {
            const flags = defaults.cs ? "g" : "gi";
            nextValue = currentValue.replace(new RegExp(payload.find, flags), replacement);
          } else if (!defaults.cs) {
            const escaped = payload.find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            nextValue = currentValue.replace(new RegExp(escaped, "gi"), replacement);
          } else {
            nextValue = currentValue.split(payload.find).join(replacement);
          }
          if (container.parent === null) {
            fail("missing_path");
          } else if (Array.isArray(container.parent)) {
            const index = Number(container.key);
            if (!Number.isInteger(index) || index < 0 || index >= container.parent.length) {
              fail("invalid_index");
              break;
            }
            container.parent[index] = nextValue;
          } else if (container.parent && typeof container.parent === "object") {
            container.parent[container.key] = nextValue;
          } else {
            fail("missing_path");
          }
          break;
        }
        case "str-slice": {
          const container = getContainer(updatedTree, pathSegments, createParents);
          if (!container || op.v === null) {
            fail("missing_path");
            break;
          }
          const currentValue = getValueAtPath(updatedTree, pathSegments);
          if (typeof currentValue !== "string") {
            fail("type_mismatch");
            break;
          }
          let payload: { start?: number; end?: number | null } = {};
          try {
            payload = JSON.parse(op.v);
          } catch {
            fail("invalid_value");
            break;
          }
          if (typeof payload.start !== "number") {
            fail("invalid_value");
            break;
          }
          const nextValue = currentValue.slice(payload.start, payload.end ?? undefined);
          if (container.parent === null) {
            fail("missing_path");
          } else if (Array.isArray(container.parent)) {
            const index = Number(container.key);
            if (!Number.isInteger(index) || index < 0 || index >= container.parent.length) {
              fail("invalid_index");
              break;
            }
            container.parent[index] = nextValue;
          } else if (container.parent && typeof container.parent === "object") {
            container.parent[container.key] = nextValue;
          } else {
            fail("missing_path");
          }
          break;
        }
        case "num-add":
        case "num-sub":
        case "num-inc":
        case "num-dec": {
          const container = getContainer(updatedTree, pathSegments, createParents);
          if (!container) {
            fail("missing_path");
            break;
          }
          const currentValue = getValueAtPath(updatedTree, pathSegments);
          if (typeof currentValue !== "number") {
            fail("type_mismatch");
            break;
          }
          let delta = 1;
          if (op.o === "num-add" || op.o === "num-sub") {
            const parsed = parseJsonValue(op.v);
            if (typeof parsed !== "number") {
              fail("invalid_value");
              break;
            }
            delta = parsed;
          } else if (op.v !== null) {
            const parsed = parseJsonValue(op.v);
            if (typeof parsed === "number") {
              delta = parsed;
            }
          }
          const nextValue =
            op.o === "num-sub" || op.o === "num-dec"
              ? currentValue - delta
              : currentValue + delta;
          if (container.parent === null) {
            fail("missing_path");
          } else if (Array.isArray(container.parent)) {
            const index = Number(container.key);
            if (!Number.isInteger(index) || index < 0 || index >= container.parent.length) {
              fail("invalid_index");
              break;
            }
            container.parent[index] = nextValue;
          } else if (container.parent && typeof container.parent === "object") {
            container.parent[container.key] = nextValue;
          } else {
            fail("missing_path");
          }
          break;
        }
        default:
          logger.warn("OrchestratorTree", "Unsupported patch op", { op });
          failureReason = "unsupported_op";
      }
    } catch (err) {
      logger.warn("OrchestratorTree", "Failed to apply patch op", { op, error: err });
      failureReason = "apply_error";
    }
    if (failureReason) break;
  }

  if (failureReason) {
    return { tree: originalTree, ok: false, failureReason };
  }

  // Increment version
  if (typeof nextVersion === "number") {
    updatedTree.metadata.version = nextVersion;
  } else {
    updatedTree.metadata.version = (updatedTree.metadata.version || 0) + 1;
  }
  updatedTree.metadata.updatedAt = new Date().toISOString();

  return { tree: updatedTree, ok: true };
}

// ============================================================================
// Slice
// ============================================================================

// Complete snapshot ordering only applies to the exact same call and tree ID.
// TreeMetadata.version is an integer >=1 in both real Python tree models.
// Safe integers are required to compare that protocol value exactly in JavaScript.
// This guard is not publisher authority and does not rank different opaque IDs.
function shouldRetainCurrentTreeSnapshot(
  current: ConversationTree | null,
  incoming: ConversationTree | null | undefined
): boolean {
  if (!current || !incoming || typeof current.id !== "string" || !current.id ||
      typeof current.callId !== "string" || !current.callId ||
      incoming.id !== current.id || incoming.callId !== current.callId) {
    return false;
  }
  const currentVersion = current.metadata?.version;
  if (typeof currentVersion !== "number" || !Number.isSafeInteger(currentVersion) || currentVersion < 1) {
    // An unknown current revision is not an ordered anchor. Preserve the
    // existing initial/reset/historical behavior rather than inventing one.
    return false;
  }
  const incomingVersion = incoming.metadata?.version;
  return typeof incomingVersion !== "number" || !Number.isSafeInteger(incomingVersion) ||
    incomingVersion < 1 || incomingVersion < currentVersion;
}

const orchestratorTreeSlice = createSlice({
  name: "orchestratorTree",
  initialState,
  reducers: {
    /**
     * Set the full conversation tree (initial load or refresh)
     */
    setTree: (state, action: PayloadAction<ConversationTree>) => {
      if (shouldRetainCurrentTreeSnapshot(state.tree, action.payload)) return;
      const isInitialLoad = state.tree === null;
      const previousExpandedIds = state.expandedNodeIds;
      const previousCurrentNodeId = state.tree?.currentNodeId;
      const now = Date.now();

      // Repair any baked-in multi-current state from old persisted snapshots
      // before assigning. Mirrors the backend safety net in tree_analyzer.py.
      const currentFixes = enforceSingleCurrent(action.payload);
      if (currentFixes > 0) {
        logger.warn("OrchestratorTree", "setTree: repaired multi-current state on rehydrate", {
          treeId: action.payload.id,
          fixes: currentFixes,
          currentNodeId: action.payload.currentNodeId,
        });
      }

      state.tree = action.payload;
      state.isLoading = false;
      state.error = null;
      state.localVersion = action.payload.metadata.version;
      state.needsFullTreeRefresh = false;

      // Track update time for stuck detection
      // Any tree data arriving means the backend is alive — unconditionally reset the timer.
      // Stuck detection monitors whether the *backend* stops sending data entirely,
      // not whether currentNodeId is progressing.
      state.lastTreeUpdateTime = now;

      // Enable stuck detection after initial tree is received (with a delay to allow first updates)
      if (isInitialLoad) {
        // Will be enabled by separate action after a grace period
        state.stuckDetectionEnabled = false;
        state.stuckRefreshAttempts = 0;
      } else if (action.payload.currentNodeId !== previousCurrentNodeId) {
        // Only reset attempts if the node actually changed
        state.stuckRefreshAttempts = 0;
      }
      // Note: if currentNodeId is unchanged, we preserve stuckRefreshAttempts
      // to allow exponential backoff to continue

      // Validate future nodes and set flag if more predictions needed
      state.needsMorePredictions = validateFutureNodes(action.payload);

      // Collect all node IDs from the new tree
      const allNodeIds = collectAllNodeIds(action.payload.rootNode);
      const smartExpandedIds = computeSmartExpandedIds(
        action.payload.rootNode,
        action.payload.currentNodeId
      );

      if (isInitialLoad) {
        // Initial load: apply smart collapse defaults
        state.expandedNodeIds = Array.from(smartExpandedIds);
      } else {
        // Refresh: preserve expanded state for existing nodes + smart-expand new nodes
        const existingExpandedIds = previousExpandedIds.filter(id => allNodeIds.includes(id));
        const newNodeIds = allNodeIds.filter(id => !previousExpandedIds.includes(id));
        const newAutoExpandedIds = newNodeIds.filter((id) => smartExpandedIds.has(id));
        state.expandedNodeIds = [...existingExpandedIds, ...newAutoExpandedIds];
      }

      logger.info("OrchestratorTree", "Tree set", {
        treeId: action.payload.id,
        version: action.payload.metadata.version,
        needsMorePredictions: state.needsMorePredictions,
        expandedCount: state.expandedNodeIds.length,
        isInitialLoad,
      });
    },

    /**
     * Apply a patch to the tree
     */
    applyPatch: (
      state,
      action: PayloadAction<{ patch: PatchBatch; version: number }>
    ) => {
      if (!state.tree) {
        logger.warn("OrchestratorTree", "Cannot apply patch, no tree exists");
        state.needsFullTreeRefresh = true;
        return;
      }

      const { patch, version } = action.payload;
      const now = Date.now();

      // Skip if version is older than current
      if (version <= state.localVersion) {
        logger.debug("OrchestratorTree", "Skipping stale patch", {
          receivedVersion: version,
          currentVersion: state.localVersion
        });
        return;
      }

      // Get all current node IDs before applying patch
      const previousNodeIds = collectAllNodeIds(state.tree.rootNode);
      const previousCurrentNodeId = state.tree.currentNodeId;

      const result = applyPatchToTree(state.tree, patch, version);
      if (!result.ok) {
        state.needsFullTreeRefresh = true;
        logger.warn("OrchestratorTree", "Patch failed, requesting full tree", {
          reason: result.failureReason,
          patchVersion: version,
          localVersion: state.localVersion,
          opsCount: patch?.ops?.length || 0,
          // Include first op for debugging
          firstOp: patch?.ops?.[0] ? {
            operation: patch.ops[0].o,
            path: patch.ops[0].p,
          } : null,
          suggestion: result.failureReason === 'missing_path'
            ? 'Tree structure may have changed - waiting for full tree refresh'
            : result.failureReason === 'test_failed'
            ? 'Concurrent modification detected - waiting for full tree refresh'
            : 'Patch application error - waiting for full tree refresh',
        });
        return;
      }

      state.tree = result.tree;
      const fixes = enforceSingleCurrent(state.tree);
      if (fixes > 0) {
        logger.warn("OrchestratorTree", "applyPatch: repaired multi-current state after patch", {
          treeId: state.tree.id,
          fixes,
          currentNodeId: state.tree.currentNodeId,
        });
      }
      state.localVersion = version;
      state.needsFullTreeRefresh = false;

      // Any patch data arriving means the backend is alive — unconditionally reset the timer.
      // Stuck detection monitors whether the backend stops sending data entirely.
      state.lastTreeUpdateTime = now;
      if (result.tree.currentNodeId !== previousCurrentNodeId) {
        state.stuckRefreshAttempts = 0;
        logger.debug("OrchestratorTree", "Node transition detected in patch", {
          from: previousCurrentNodeId,
          to: result.tree.currentNodeId,
        });
      }

      // Validate future nodes after patch
      state.needsMorePredictions = validateFutureNodes(result.tree);

      // Expand any NEW nodes that were added by the patch
      const currentNodeIds = collectAllNodeIds(result.tree.rootNode);
      const newNodeIds = currentNodeIds.filter(id => !previousNodeIds.includes(id));
      let newNodesExpanded = 0;
      if (newNodeIds.length > 0) {
        const smartExpandedIds = computeSmartExpandedIds(
          result.tree.rootNode,
          result.tree.currentNodeId
        );
        const newAutoExpandedIds = newNodeIds.filter((id) =>
          smartExpandedIds.has(id)
        );
        if (newAutoExpandedIds.length > 0) {
          state.expandedNodeIds = [...state.expandedNodeIds, ...newAutoExpandedIds];
          newNodesExpanded = newAutoExpandedIds.length;
        }
      }

      logger.info("OrchestratorTree", "Patch applied", {
        opsCount: patch.ops.length,
        newVersion: version,
        needsMorePredictions: state.needsMorePredictions,
        newNodesExpanded,
      });
    },

    /**
     * Toggle expand/collapse for a node
     */
    toggleNodeExpanded: (state, action: PayloadAction<string>) => {
      const nodeId = action.payload;
      const index = state.expandedNodeIds.indexOf(nodeId);

      if (index >= 0) {
        state.expandedNodeIds.splice(index, 1);
      } else {
        state.expandedNodeIds.push(nodeId);
      }
    },

    /**
     * Expand a specific node
     */
    expandNode: (state, action: PayloadAction<string>) => {
      const nodeId = action.payload;
      if (!state.expandedNodeIds.includes(nodeId)) {
        state.expandedNodeIds.push(nodeId);
      }
    },

    /**
     * Collapse a specific node
     */
    collapseNode: (state, action: PayloadAction<string>) => {
      const index = state.expandedNodeIds.indexOf(action.payload);
      if (index >= 0) {
        state.expandedNodeIds.splice(index, 1);
      }
    },

    /**
     * Set expanded node IDs directly (for smart collapse)
     * This replaces the current expanded IDs with the provided set
     */
    setExpandedNodeIds: (state, action: PayloadAction<string[]>) => {
      state.expandedNodeIds = action.payload;
      logger.debug("OrchestratorTree", "Expanded node IDs set", {
        count: action.payload.length,
      });
    },

    /**
     * Merge expanded node IDs (add to existing without removing user's choices)
     * Used to add smart-expand nodes while preserving user's manual expansions
     */
    mergeExpandedNodeIds: (state, action: PayloadAction<string[]>) => {
      const newIds = action.payload.filter(
        (id) => !state.expandedNodeIds.includes(id)
      );
      if (newIds.length > 0) {
        state.expandedNodeIds.push(...newIds);
        logger.debug("OrchestratorTree", "Merged expanded node IDs", {
          added: newIds.length,
          total: state.expandedNodeIds.length,
        });
      }
    },

    /**
     * Expand all nodes
     */
    expandAll: (state) => {
      if (!state.tree) return;

      const allIds: string[] = [];
      const collectIds = (node: TreeNode) => {
        allIds.push(node.id);
        (node.children || []).forEach(collectIds);
      };
      collectIds(state.tree.rootNode);

      state.expandedNodeIds = allIds;
    },

    /**
     * Collapse all nodes (except root)
     */
    collapseAll: (state) => {
      if (!state.tree) return;
      state.expandedNodeIds = [state.tree.rootNode.id];
    },

    /**
     * Select a node for the detail panel
     */
    selectNode: (state, action: PayloadAction<string | null>) => {
      state.selectedNodeId = action.payload;
      state.isDetailPanelOpen = action.payload !== null;
    },

    /**
     * Toggle inline detail expansion for a node
     */
    toggleDetailExpanded: (state, action: PayloadAction<string>) => {
      const nodeId = action.payload;
      const index = state.detailExpandedNodeIds.indexOf(nodeId);
      if (index >= 0) {
        state.detailExpandedNodeIds.splice(index, 1);
      } else {
        state.detailExpandedNodeIds.push(nodeId);
      }
    },

    /**
     * Collapse detail expansion for a specific node
     */
    collapseDetailExpanded: (state, action: PayloadAction<string>) => {
      const index = state.detailExpandedNodeIds.indexOf(action.payload);
      if (index >= 0) {
        state.detailExpandedNodeIds.splice(index, 1);
      }
    },

    /**
     * Close the detail panel
     */
    closeDetailPanel: (state) => {
      state.isDetailPanelOpen = false;
      state.selectedNodeId = null;
    },

    /**
     * Toggle strategy panel collapsed state
     */
    toggleStrategyPanel: (state) => {
      state.isStrategyPanelCollapsed = !state.isStrategyPanelCollapsed;
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Set error state
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Reset tree state (on call end)
     */
    resetTree: (state) => {
      state.tree = null;
      state.isLoading = false;
      state.error = null;
      state.expandedNodeIds = [];
      state.detailExpandedNodeIds = [];
      state.selectedNodeId = null;
      state.isDetailPanelOpen = false;
      state.localVersion = 0;
      state.needsFullTreeRefresh = false;
      // Reset stuck detection state
      state.lastTreeUpdateTime = 0;
      state.stuckDetectionEnabled = false;
      state.stuckRefreshAttempts = 0;
      logger.info("OrchestratorTree", "Tree reset");
    },

    /**
     * Handle WebSocket message (full or patch)
     */
    handleTreeMessage: (state, action: PayloadAction<TreeMessage>) => {
      const message = action.payload;
      const now = Date.now();

      if (message.type === "full") {
        if (shouldRetainCurrentTreeSnapshot(state.tree, message.tree)) return;
        const isInitialLoad = state.tree === null;
        const previousExpandedIds = state.expandedNodeIds;
        const previousCurrentNodeId = state.tree?.currentNodeId;

        // Repair multi-current before assigning — same as setTree and the
        // "patch" branch below. This was the ONE tree assignment of the four
        // that skipped it, and it is the LIVE path: during a call the tree
        // arrives here, not through setTree. A broadcast that promotes a new
        // current without demoting the old one left two status="current" nodes
        // in the store, and the canvas (TreeNode.tsx) styles "active" straight
        // off node.status — so every leaked node rendered active. The MiniMap
        // hid the symptom because it gates on the singleton tree.currentNodeId,
        // which is why this read as a canvas-only bug.
        const fullFixes = enforceSingleCurrent(message.tree);
        if (fullFixes > 0) {
          logger.warn("OrchestratorTree", "handleTreeMessage: repaired multi-current state on full tree", {
            treeId: message.tree.id,
            fixes: fullFixes,
            currentNodeId: message.tree.currentNodeId,
          });
        }

        state.tree = message.tree;
        state.isLoading = false;
        state.error = null;
        state.localVersion = message.tree.metadata.version;
        state.needsFullTreeRefresh = false;

        // Any valid tree message means the backend is alive. Reset the update
        // timer even when the current node stays the same, but only clear the
        // stuck-attempt backoff on initial load or real node progression.
        state.lastTreeUpdateTime = now;

        if (isInitialLoad) {
          state.stuckDetectionEnabled = false;
          state.stuckRefreshAttempts = 0;
        } else if (message.tree.currentNodeId !== previousCurrentNodeId) {
          state.stuckRefreshAttempts = 0;
          logger.debug("OrchestratorTree", "Node progressed, reset stuck backoff", {
            from: previousCurrentNodeId,
            to: message.tree.currentNodeId,
          });
        }
        // If currentNodeId is unchanged, preserve stuckRefreshAttempts so
        // repeated refreshes still back off.

        // Validate future nodes
        state.needsMorePredictions = validateFutureNodes(message.tree);

        // Collect all node IDs from the new tree
        const allNodeIds = collectAllNodeIds(message.tree.rootNode);
        const smartExpandedIds = computeSmartExpandedIds(
          message.tree.rootNode,
          message.tree.currentNodeId
        );

        if (isInitialLoad) {
          // Initial load: apply smart collapse defaults
          state.expandedNodeIds = Array.from(smartExpandedIds);
        } else {
          // Refresh: preserve expanded state for existing nodes + smart-expand new nodes
          const existingExpandedIds = previousExpandedIds.filter(id => allNodeIds.includes(id));
          const newNodeIds = allNodeIds.filter(id => !previousExpandedIds.includes(id));
          const newAutoExpandedIds = newNodeIds.filter((id) => smartExpandedIds.has(id));
          state.expandedNodeIds = [...existingExpandedIds, ...newAutoExpandedIds];
        }

        logger.info("OrchestratorTree", "Full tree received", {
          treeId: message.tree.id,
          totalNodes: allNodeIds.length,
          needsMorePredictions: state.needsMorePredictions,
          expandedCount: state.expandedNodeIds.length,
          isInitialLoad,
        });
      } else if (message.type === "patch") {
        if (!state.tree) {
          logger.warn("OrchestratorTree", "Received patch but no tree exists");
          state.needsFullTreeRefresh = true;
          return;
        }

        if (message.version <= state.localVersion) {
          logger.debug("OrchestratorTree", "Skipping stale patch");
          return;
        }

        // Track previous state for transition detection
        const previousCurrentNodeId = state.tree.currentNodeId;

        const result = applyPatchToTree(state.tree, message.patch, message.version);
        if (!result.ok) {
          state.needsFullTreeRefresh = true;
          logger.warn("OrchestratorTree", "Patch failed, requesting full tree", {
            reason: result.failureReason,
            patchVersion: message.version,
            localVersion: state.localVersion,
            opsCount: message.patch?.ops?.length || 0,
            // Include first op for debugging
            firstOp: message.patch?.ops?.[0] ? {
              operation: message.patch.ops[0].o,
              path: message.patch.ops[0].p,
            } : null,
            suggestion: result.failureReason === 'missing_path'
              ? 'Tree structure may have changed - waiting for full tree refresh'
              : result.failureReason === 'test_failed'
              ? 'Concurrent modification detected - waiting for full tree refresh'
              : 'Patch application error - waiting for full tree refresh',
          });
          return;
        }

        // Get all current node IDs before updating tree
        const previousNodeIds = collectAllNodeIds(state.tree.rootNode);

        state.tree = result.tree;
        const fixes = enforceSingleCurrent(state.tree);
        if (fixes > 0) {
          logger.warn("OrchestratorTree", "applyPatch: repaired multi-current state after patch", {
            treeId: state.tree.id,
            fixes,
            currentNodeId: state.tree.currentNodeId,
          });
        }
        state.localVersion = message.version;
        state.needsFullTreeRefresh = false;

        // Any applied patch means the backend is alive. Reset the update timer
        // even if this is a metadata/content patch with the same current node.
        state.lastTreeUpdateTime = now;
        if (result.tree.currentNodeId !== previousCurrentNodeId) {
          state.stuckRefreshAttempts = 0;
          logger.debug("OrchestratorTree", "Node transition in handleTreeMessage", {
            from: previousCurrentNodeId,
            to: result.tree.currentNodeId,
          });
        }

        // Validate future nodes after patch
        state.needsMorePredictions = validateFutureNodes(result.tree);

        // Expand any NEW nodes that were added by the patch
        const currentNodeIds = collectAllNodeIds(result.tree.rootNode);
        const newNodeIds = currentNodeIds.filter(id => !previousNodeIds.includes(id));
        let newNodesExpanded = 0;
        if (newNodeIds.length > 0) {
          const smartExpandedIds = computeSmartExpandedIds(
            result.tree.rootNode,
            result.tree.currentNodeId
          );
          const newAutoExpandedIds = newNodeIds.filter((id) =>
            smartExpandedIds.has(id)
          );
          if (newAutoExpandedIds.length > 0) {
            state.expandedNodeIds = [...state.expandedNodeIds, ...newAutoExpandedIds];
            newNodesExpanded = newAutoExpandedIds.length;
          }
        }

        logger.info("OrchestratorTree", "Patch applied", {
          opsCount: message.patch.ops.length,
          needsMorePredictions: state.needsMorePredictions,
          newNodesExpanded,
        });
      } else if (message.type === "need_tree_seed") {
        // Backend has no tree (worker restart) - needs us to seed it with our cached tree
        if (state.tree) {
          state.needsTreeSeed = true;
          logger.info("OrchestratorTree", "Backend needs tree seed - will send cached tree", {
            treeId: state.tree.id,
            version: state.localVersion,
          });
        } else {
          logger.warn("OrchestratorTree", "Backend needs tree seed but we have no cached tree");
        }
      }
    },

    /**
     * Clear full refresh request flag
     */
    clearTreeRefreshNeeded: (state) => {
      state.needsFullTreeRefresh = false;
    },

    /**
     * Clear more predictions needed flag (after request sent)
     */
    clearMorePredictionsNeeded: (state) => {
      state.needsMorePredictions = false;
    },

    /**
     * Clear tree seed needed flag (after seed sent)
     */
    clearTreeSeedNeeded: (state) => {
      state.needsTreeSeed = false;
    },

    /**
     * Enable stuck detection after initial grace period.
     * Called from Conference.tsx after tree is loaded and grace period elapsed.
     */
    enableStuckDetection: (state) => {
      if (state.tree) {
        state.stuckDetectionEnabled = true;
        state.lastTreeUpdateTime = Date.now(); // Reset timer when enabling
        logger.info("OrchestratorTree", "Stuck detection enabled", {
          treeId: state.tree.id,
          currentNodeId: state.tree.currentNodeId,
        });
      }
    },

    /**
     * Disable stuck detection (e.g., when call ends).
     */
    disableStuckDetection: (state) => {
      state.stuckDetectionEnabled = false;
      state.stuckRefreshAttempts = 0;
    },

    /**
     * Mark tree as stuck and request refresh with exponential backoff.
     * Called by stuck detection effect when tree hasn't updated.
     */
    markTreeStuck: (state) => {
      if (!state.tree || !state.stuckDetectionEnabled) {
        return;
      }

      state.stuckRefreshAttempts += 1;
      state.needsFullTreeRefresh = true;

      // Get current node title for debugging (find node by currentNodeId)
      const currentNode = findNodeById(state.tree.rootNode, state.tree.currentNodeId);
      logger.warn("OrchestratorTree", "Tree detected as stuck, requesting refresh", {
        treeId: state.tree.id,
        currentNodeId: state.tree.currentNodeId,
        currentNodeTitle: currentNode?.title ?? "unknown",
        stuckRefreshAttempts: state.stuckRefreshAttempts,
        lastTreeUpdateTime: state.lastTreeUpdateTime,
        timeSinceUpdate: Date.now() - state.lastTreeUpdateTime,
      });
    },

    /**
     * Record a user interaction with the tree view
     */
    recordInteraction: (state) => {
      state.lastInteractionTime = Date.now();
      state.isUserInteracting = true;
      state.autoViewEnabled = false;
    },

    /**
     * Enable auto-view centering (after inactivity timeout)
     */
    enableAutoView: (state) => {
      state.autoViewEnabled = true;
      state.isUserInteracting = false;
    },

    /**
     * Disable auto-view centering (user took control)
     */
    disableAutoView: (state) => {
      state.autoViewEnabled = false;
    },

    /**
     * Record a node transition (currentNodeId changed).
     * This is called when the orchestrator detects the current node has changed.
     * Used to trigger downstream updates like action regeneration.
     */
    nodeTransitionOccurred: (
      state,
      action: PayloadAction<{
        previousNodeId: string;
        currentNodeId: string;
        timestamp: number;
      }>
    ) => {
      state.previousCurrentNodeId = action.payload.previousNodeId;
      state.lastTransitionTime = action.payload.timestamp;

      logger.info("OrchestratorTree", "Node transition occurred", {
        previousNodeId: action.payload.previousNodeId,
        currentNodeId: action.payload.currentNodeId,
        timestamp: action.payload.timestamp,
      });
    },

    /**
     * Hydrate tree state from saved data (localStorage or database)
     * Used when navigating to incident report after call ends
     */
    hydrateOrchestratorTreeState: (
      _state,
      action: PayloadAction<Partial<OrchestratorTreeState>>
    ) => {
      const savedState = action.payload;
      if (shouldRetainCurrentTreeSnapshot(_state.tree, savedState.tree)) return;
      logger.info("OrchestratorTree", "Hydrating tree state from saved data", {
        hasTree: !!savedState.tree,
        expandedNodeCount: savedState.expandedNodeIds?.length || 0,
      });
      // Merge with initialState to ensure all fields have defaults
      return {
        ...initialState,
        ...savedState,
        // Reset interaction states since this is a historical view
        isLoading: false,
        needsFullTreeRefresh: false,
        needsMorePredictions: false,
        needsTreeSeed: false,
        isUserInteracting: false,
        autoViewEnabled: true,
        // Disable stuck detection for historical views
        stuckDetectionEnabled: false,
        stuckRefreshAttempts: 0,
        lastTreeUpdateTime: 0,
      };
    },
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  setTree,
  applyPatch,
  toggleNodeExpanded,
  expandNode,
  collapseNode,
  setExpandedNodeIds,
  mergeExpandedNodeIds,
  expandAll,
  collapseAll,
  selectNode,
  toggleDetailExpanded,
  collapseDetailExpanded,
  closeDetailPanel,
  toggleStrategyPanel,
  setLoading,
  setError,
  resetTree,
  handleTreeMessage,
  clearTreeRefreshNeeded,
  clearMorePredictionsNeeded,
  clearTreeSeedNeeded,
  enableStuckDetection,
  disableStuckDetection,
  markTreeStuck,
  recordInteraction,
  enableAutoView,
  disableAutoView,
  nodeTransitionOccurred,
  hydrateOrchestratorTreeState,
} = orchestratorTreeSlice.actions;

export default orchestratorTreeSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

import type { RootState } from "../store";

export const selectTree = (state: RootState) => state.orchestratorTree.tree;
export const selectTreeLoading = (state: RootState) =>
  state.orchestratorTree.isLoading;
export const selectTreeError = (state: RootState) =>
  state.orchestratorTree.error;
export const selectExpandedNodeIds = (state: RootState) =>
  state.orchestratorTree.expandedNodeIds;
export const selectSelectedNodeId = (state: RootState) =>
  state.orchestratorTree.selectedNodeId;
export const selectIsDetailPanelOpen = (state: RootState) =>
  state.orchestratorTree.isDetailPanelOpen;
export const selectIsStrategyPanelCollapsed = (state: RootState) =>
  state.orchestratorTree.isStrategyPanelCollapsed;
export const selectNeedsFullTreeRefresh = (state: RootState) =>
  state.orchestratorTree.needsFullTreeRefresh;
export const selectNeedsMorePredictions = (state: RootState) =>
  state.orchestratorTree.needsMorePredictions;
export const selectNeedsTreeSeed = (state: RootState) =>
  state.orchestratorTree.needsTreeSeed;
export const selectLastInteractionTime = (state: RootState) =>
  state.orchestratorTree.lastInteractionTime;
export const selectIsUserInteracting = (state: RootState) =>
  state.orchestratorTree.isUserInteracting;
export const selectAutoViewEnabled = (state: RootState) =>
  state.orchestratorTree.autoViewEnabled;
export const selectLastTreeUpdateTime = (state: RootState) =>
  state.orchestratorTree.lastTreeUpdateTime;
export const selectStuckDetectionEnabled = (state: RootState) =>
  state.orchestratorTree.stuckDetectionEnabled;
export const selectStuckRefreshAttempts = (state: RootState) =>
  state.orchestratorTree.stuckRefreshAttempts;
export const selectDetailExpandedNodeIds = (state: RootState) =>
  state.orchestratorTree.detailExpandedNodeIds;

/**
 * Select the currently selected node (if any)
 */
export const selectSelectedNode = (state: RootState): TreeNode | null => {
  const tree = state.orchestratorTree.tree;
  const nodeId = state.orchestratorTree.selectedNodeId;

  if (!tree || !nodeId) return null;
  return findNodeById(tree.rootNode, nodeId);
};

/**
 * Select the current strategy
 */
export const selectStrategy = (
  state: RootState
): ConversationStrategy | null => {
  return state.orchestratorTree.tree?.strategy ?? null;
};

/**
 * Select the current node (marked as "current" in the tree)
 */
export const selectCurrentNode = (state: RootState): TreeNode | null => {
  const tree = state.orchestratorTree.tree;
  if (!tree) return null;
  return findNodeById(tree.rootNode, tree.currentNodeId);
};
